import { createContext, ReactElement, ReactNode, useState } from 'react';

import { ICityResult, IPlaceResult } from 'interfaces/Address';
import { IBusiness } from 'interfaces/Business';
import { WebsiteValidationResult } from 'interfaces/WebsiteValidation';

import ApiService from 'services/api';
import { IVoiceResult } from 'interfaces/VoiceResult';
import { logger, ErrorType } from '../utils/logger';

interface IBusinessContext {
  actions: {
    getVoices: () => Promise<IVoiceResult[]>;
    setBusiness: (business: IBusiness) => void;
    getBusinesses: (input: string) => Promise<IBusiness[]>;
    validateWebsite: (website: string, signal?: AbortSignal) => Promise<WebsiteValidationResult>;
    getPlaceDetails: (placeId: string) => Promise<IPlaceResult>;
  };
  data: {
    voices: IVoiceResult[];
    business: IBusiness | null;
  };
}

interface IBusinessProvider {
  children: ReactNode;
}

const BusinessContext = createContext<IBusinessContext>({
  actions: {
    getVoices: async () => {
      return [] as IVoiceResult[];
    },
    setBusiness: () => {},
    getBusinesses: async () => {
      return [] as IBusiness[];
    },
    getPlaceDetails: async () => {
      return {} as IPlaceResult;
    },
    validateWebsite: async () => {
      return {} as WebsiteValidationResult;
    },
  },
  data: {
    voices: [],
    business: null,
  },
});

export function BusinessProvider(props: IBusinessProvider): ReactElement {
  const { children } = props;
  const [business, setBusiness] = useState<IBusiness | null>(null);
  const [voices, setVoices] = useState<IVoiceResult[]>([]);

  const getBusinesses = async (input: string) => {
    try {
      const response = await ApiService.get('locations/place/autocomplete', {
        params: { input },
      });

      return response.data.places || [];
    } catch (e: any) {
      logger.error('Failed to search businesses', e as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'getBusinesses',
        metadata: { input }
      });
      throw e;
    }
  };

  const getPlaceDetails = async (placeId: string) => {
    try {
      const response = await ApiService.get('locations/place/details', {
        params: { place_id: placeId },
      });

      const result = response.data;
      let componentsByType = new Map();

      for (var i = 0; i < result.addressComponents.length; i++) {
        var c = result.addressComponents[i];
        componentsByType.set(c.types[0], c);
      }

      const location: ICityResult = {
        latitude: result?.location?.latitude || 0,
        longitude: result?.location?.longitude || 0,
        city: componentsByType.get('locality')?.shortText,
        country: componentsByType.get('country')?.shortText,
        region: componentsByType.get('administrative_area_level_1')?.shortText,
      };

      return { ...result, location };
    } catch (e: any) {
      logger.error('Failed to get place details', e as Error, {
        errorType: ErrorType.API_ERROR,
        component: 'getPlaceDetails',
        metadata: { placeId }
      });
      throw e;
    }
  };

  /**
   * Validates a website URL for reachability, content parsability, and SPA detection.
   * 
   * This function integrates with the backend validation service to assess whether
   * a website is suitable for AI training data extraction. It handles various edge cases
   * and provides detailed feedback about why validation might fail.
   * 
   * @param website - The website URL to validate
   * @param signal - AbortSignal for request cancellation
   * @returns WebsiteValidationResult containing validation status and metadata
   * 
   * Validation Criteria:
   * - URL reachability (can we connect to it?)
   * - Content parsability (can we extract meaningful data?)
   * - SPA detection (is it a Single Page Application?)
   * - Response status codes and final redirected URLs
   * 
   * Error Handling Strategy:
   * - Request cancellation: Re-throw for caller to handle
   * - Network/API errors: Return consistent error object with details
   * - Graceful degradation: Always return a result, never completely fail
   * 
   * @example
   * try {
   *   const result = await validateWebsite('https://example.com');
   *   if (result.valid && result.parsable) {
   *     // Proceed with website
   *   } else if (result.is_spa) {
   *     // Handle SPA case
   *   }
   * } catch (error) {
   *   // Handle cancellation
   * }
   */
  const validateWebsite = async (website: string, signal?: AbortSignal) => {
    try {
      const response = await ApiService.get('user/validate-website', {
        params: { url: website },
        signal: signal
      });
      
      // Return the full validation response from the new API
      return response.data;
    } catch (e: any) {
      // Handle different types of cancellation/abortion
      if (signal?.aborted || e.code === 'ERR_CANCELED' || e.response?.status === 499) {
        logger.debug('Website validation request cancelled', {
          reason: e.response?.status === 499 ? 'Server detected disconnection' : 'Client cancelled'
        });
        throw e; // Let the calling code handle cancellation
      }
      
      logger.error('Website validation failed', e, {
        errorType: ErrorType.VALIDATION_ERROR,
        component: 'validateWebsite'
      });
      // Return a consistent error response structure
      return {
        valid: false,
        reachable: false,
        parsable: false,
        final_url: null,
        status_code: null,
        title: '',
        has_content: false,
        is_spa: false,
        spa_info: {
          is_spa: false,
          confidence_score: 0,
          indicators: [],
          frameworks: [],
          recommendation: 'Verify URL and try again'
        },
        error: e.response?.data?.detail || 'Website validation failed',
        strategy_used: null
      };
    }
  };

  const getVoices = async () => {
    try {
      const response = await ApiService.get(`voices`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const voices: IVoiceResult[] = response.data;
      if (voices) {
        const sorted = voices.sort((a, b) => {
          if (a.is_premium === b.is_premium) return a.name > b.name ? 1 : -1;
          return a.is_premium && !b.is_premium ? 1 : -1;
        });
        setVoices(sorted);
        return sorted;
      } else {
        return [];
      }
    } catch (e: any) {
      throw e;
    }
  };

  return (
    <BusinessContext.Provider
      value={{
        data: {
          voices,
          business,
        },
        actions: {
          getVoices,
          setBusiness,
          getBusinesses,
          getPlaceDetails,
          validateWebsite,
        },
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
}

export default BusinessContext;
